import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#050505]">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-2">Get in Touch</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8]">
            Contact Us
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-medium text-[#F8F8F8] mb-6">Get in Touch</h2>
            <p className="text-[#A0A0A0] leading-relaxed mb-8">
              Have questions about our PPF kits or need help choosing the right product for your vehicle? 
              Our team is here to help. Reach out to us and we'll get back to you as soon as possible.
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#111111] border border-[#222222] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-[#F8F8F8] font-medium">Email</p>
                  <a href="mailto:Sales@DIYPPFKit.com.au" className="text-[#A0A0A0] hover:text-[#00F0FF] transition-colors">
                    Sales@DIYPPFKit.com.au
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#111111] border border-[#222222] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-[#F8F8F8] font-medium">Phone</p>
                  <a href="tel:0411911858" className="text-[#A0A0A0] hover:text-[#00F0FF] transition-colors">
                    0411 911 858
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#111111] border border-[#222222] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-[#F8F8F8] font-medium">Postal Address</p>
                  <p className="text-[#A0A0A0]">
                    PO Box 426<br />
                    St Ives NSW 2075<br />
                    Australia
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-[#111111] border border-[#222222]">
              <h3 className="text-[#F8F8F8] font-medium mb-3">Business Hours</h3>
              <p className="text-[#A0A0A0] text-sm">
                Monday - Friday: 9:00 AM - 5:00 PM AEST<br />
                Saturday - Sunday: Closed
              </p>
              <p className="text-[#666666] text-sm mt-3">
                We typically respond to enquiries within 24-48 business hours.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#111111] border border-[#222222] p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#34C759] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#050505]" />
                </div>
                <h3 className="text-xl font-medium text-[#F8F8F8] mb-3">Message Sent!</h3>
                <p className="text-[#A0A0A0] mb-6">
                  Thank you for contacting us. We'll get back to you as soon as possible.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="border-[#444444] text-[#F8F8F8] hover:bg-[#1A1A1A] px-6 py-2 rounded-none"
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-medium text-[#F8F8F8] mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label className="text-[#A0A0A0]">Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Phone</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] h-12 rounded-none mt-2"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0A0A0]">Message *</Label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-[#1A1A1A] border-[#333333] text-[#F8F8F8] rounded-none mt-2 resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-6 rounded-none"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
